import { useState, useEffect, useMemo, useRef } from 'react';
import { dbSurgeries, dbNotes, type Surgery, type PatientNote } from '../../lib/localDB';
import { pricingDB, type PricingConfig } from '../../lib/pricingDB';
import { cariDB } from '../../lib/cariDB';
import { useNavigate } from 'react-router-dom';
import ExportModal from './components/ExportModal';
import ImportModal from './components/ImportModal';
import VDKLegendModal from '../../components/feature/VDKLegendModal';
import CariClinicModal from '../cari/components/CariClinicModal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import SendMessageModal from '../../components/feature/SendMessageModal';

type SurgeryGroup = {
  patientName: string;
  surgeries: Surgery[];
  notes: PatientNote[];
};

export default function Dashboard() {
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [patientNotes, setPatientNotes] = useState<PatientNote[]>([]);
  const [cariTxs, setCariTxs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());
  const [notesPopupPatient, setNotesPopupPatient] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState<PatientNote | null>(null);
  const [notePatientId, setNotePatientId] = useState('');
  const [noteFormData, setNoteFormData] = useState({
    note_date: new Date().toISOString().split('T')[0],
    note_type: 'general' as PatientNote['note_type'],
    title: '',
    content: '',
  });
  const [savingNote, setSavingNote] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCariModal, setShowCariModal] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const [messagingPatient, setMessagingPatient] = useState<{name: string, phone: string} | null>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [pricing, setPricing] = useState<PricingConfig>(() => pricingDB.get());
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target as Node)) {
        setShowToolsMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allSurgeries, allNotes, allCariTxs, freshPricing] = await Promise.all([
        dbSurgeries.getAll(),
        dbNotes.getAll(),
        cariDB.getAll(),
        pricingDB.load(),
      ]);
      setSurgeries(allSurgeries);
      setPatientNotes(allNotes);
      setCariTxs(allCariTxs);
      setPricing(freshPricing);
      const names = new Set<string>();
      allSurgeries.forEach((s) => names.add(s.patient_name || 'Adsız'));
      setExpandedPatients(names);
    } catch (e) {
      console.error('Veri yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  };

  const patientGroups = useMemo<SurgeryGroup[]>(() => {
    const map = new Map<string, Surgery[]>();
    surgeries.forEach((s) => {
      const key = s.patient_name?.trim() || 'Adsız';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    const groups: SurgeryGroup[] = [];
    map.forEach((surgs, name) => {
      surgs.sort(
        (a, b) => new Date(b.operation_date).getTime() - new Date(a.operation_date).getTime()
      );
      groups.push({
        patientName: name,
        surgeries: surgs,
        notes: patientNotes.filter((n) => n.patient_id === name),
      });
    });
    groups.sort((a, b) => {
      const aDate = a.surgeries[0]?.operation_date ?? '';
      const bDate = b.surgeries[0]?.operation_date ?? '';
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
    return groups;
  }, [surgeries, patientNotes]);

  const filteredGroups = useMemo(() => {
    return patientGroups.filter((g) => {
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchName = g.patientName.toLowerCase().includes(q);
        const matchClinic = g.surgeries.some((s) =>
          (s.clinic || '').toLowerCase().includes(q)
        );
        const matchDate = g.surgeries.some((s) =>
          new Date(s.operation_date).toLocaleDateString('tr-TR').includes(q)
        );
        const matchVDK = g.surgeries.some((s) =>
          s.teeth.some((t) => t.vdk.toLowerCase().includes(q))
        );
        if (!matchName && !matchClinic && !matchDate && !matchVDK) return false;
      }
      return true;
    });
  }, [patientGroups, searchQuery]);

  const totalTeeth = surgeries.reduce((sum, s) => sum + (s.teeth?.length ?? 0), 0);
  const totalNotes = patientNotes.length;
  const isFiltering = searchQuery.trim() !== '';

  const togglePatient = (name: string) => {
    setExpandedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };



  const openNoteModal = (patientName: string, note?: PatientNote) => {
    setNotePatientId(patientName);
    if (note) {
      setEditingNote(note);
      setNoteFormData({ note_date: note.note_date, note_type: note.note_type, title: note.title, content: note.content });
    } else {
      setEditingNote(null);
      setNoteFormData({ note_date: new Date().toISOString().split('T')[0], note_type: 'general', title: '', content: '' });
    }
    setShowNoteModal(true);
  };

  const closeNoteModal = () => {
    setShowNoteModal(false);
    setEditingNote(null);
    setNotePatientId('');
    setNoteFormData({ note_date: new Date().toISOString().split('T')[0], note_type: 'general', title: '', content: '' });
  };

  const handleSaveNote = async () => {
    if (!notePatientId || !noteFormData.title.trim() || !noteFormData.content.trim()) return;
    setSavingNote(true);
    try {
      if (editingNote) {
        await dbNotes.update(editingNote.id, { note_date: noteFormData.note_date, note_type: noteFormData.note_type, title: noteFormData.title.trim(), content: noteFormData.content.trim() });
        setSuccessMessage('Not güncellendi!');
      } else {
        await dbNotes.insert({ patient_id: notePatientId, note_date: noteFormData.note_date, note_type: noteFormData.note_type, title: noteFormData.title.trim(), content: noteFormData.content.trim() });
        setSuccessMessage('Not eklendi!');
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
      closeNoteModal();
    } catch (e) {
      console.error('Not kaydetme hatası:', e);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    setConfirmAction({
      title: 'Notu Sil',
      message: 'Bu notu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await dbNotes.delete(noteId);
          setSuccessMessage('Not silindi!');
          setTimeout(() => setSuccessMessage(''), 3000);
          await loadData();
        } catch (e) {
          console.error('Not silme hatası:', e);
        }
      },
    });
  };

  const handleDeleteSurgery = (id: string, patientName?: string) => {
    setConfirmAction({
      title: 'Kaydı Sil',
      message: `${patientName ? `"${patientName}" hastasına ait bu ameliyat kaydını` : 'Bu kaydı'} silmek istediğinizden emin misiniz? İlişkili cari kayıtlar da silinecektir. Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          // Cascading: delete related cari transactions
          const cariTxs = await cariDB.getSurgeryTransactions(id);
          await Promise.all(cariTxs.map((tx) => cariDB.delete(tx.id)));
          // Delete surgery
          await dbSurgeries.delete(id);
          setSuccessMessage('Kayıt ve ilişkili cari kayıtlar silindi!');
          setTimeout(() => setSuccessMessage(''), 3000);
          await loadData();
        } catch (e) {
          console.error('Silme hatası:', e);
        }
      },
    });
  };

  const handleFailTooth = async (surgery: Surgery, toothNumber: number) => {
    const updatedTeeth = surgery.teeth.map((t) => {
      if (t.tooth_number !== toothNumber) return t;
      if (t.failed) {
        return { ...t, failed: false, fail_date: null, fail_days: null };
      }
      const failDate = new Date().toISOString().split('T')[0];
      const diffDays = Math.round(
        (new Date(failDate).getTime() - new Date(surgery.operation_date).getTime()) /
        (1000 * 60 * 60 * 24)
      );
      return { ...t, failed: true, fail_date: failDate, fail_days: diffDays };
    });
    try {
      await dbSurgeries.update(surgery.id, { teeth: updatedTeeth });
      setSuccessMessage('Fail kaydı güncellendi!');
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
    } catch (e) {
      console.error('Fail güncelleme hatası:', e);
    }
  };

  const getNoteTypeLabel = (type: PatientNote['note_type']) => {
    const labels: Record<PatientNote['note_type'], string> = { general: 'Genel', checkup: 'Kontrol', complication: 'Komplikasyon', treatment: 'Tedavi', followup: 'Takip' };
    return labels[type] || type;
  };

  const getNoteTypeColor = (type: PatientNote['note_type']) => {
    const colors: Record<PatientNote['note_type'], string> = { general: 'bg-gray-100 text-gray-700', checkup: 'bg-teal-100 text-teal-700', complication: 'bg-red-100 text-red-700', treatment: 'bg-amber-100 text-amber-700', followup: 'bg-cyan-100 text-cyan-700' };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getCinsiyetLabel = (c: string) => c === 'k' ? 'Kadın' : c === 'e' ? 'Erkek' : c;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* NAV */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-tooth-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">IMPLOG</h1>
              <p className="text-xs text-gray-500">Kayıtlarınızı Yönetin</p>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              <button onClick={() => navigate('/appointments')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-calendar-todo-line text-lg"></i>Tedavi Takibi
              </button>
              <button onClick={() => navigate('/calendar')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-calendar-2-line text-lg"></i>Takvim
              </button>

              {/* Araçlar Dropdown */}
              <div className="relative" ref={toolsMenuRef}>
                <button
                  onClick={() => setShowToolsMenu((v) => !v)}
                  className={`px-4 py-2.5 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm border ${showToolsMenu ? 'bg-gray-100 text-gray-900 border-gray-300' : 'text-gray-700 hover:bg-gray-100 border-transparent'}`}
                >
                  <i className="ri-apps-2-line text-lg"></i>
                  Araçlar
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${showToolsMenu ? 'rotate-180' : ''}`}></i>
                </button>

                {showToolsMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl overflow-hidden z-50" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div className="py-1">
                      <button onClick={() => { navigate('/analytics'); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg"><i className="ri-bar-chart-line text-teal-600 text-sm"></i></div>
                        Analizler
                      </button>
                      <button onClick={() => { navigate('/cari'); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg"><i className="ri-bank-line text-teal-600 text-sm"></i></div>
                        Cari Hesaplar
                      </button>
                      <button onClick={() => { setShowCariModal(true); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg"><i className="ri-add-circle-line text-teal-600 text-sm"></i></div>
                        Cari Ekle
                      </button>
                      <button onClick={() => { navigate('/sms-templates'); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-violet-50 rounded-lg"><i className="ri-message-2-line text-violet-600 text-sm"></i></div>
                        Mesaj Şablonları
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button onClick={() => { navigate('/pricing'); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg"><i className="ri-price-tag-3-line text-amber-600 text-sm"></i></div>
                        Ücretler
                      </button>
                      <button onClick={() => { setShowExportModal(true); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg"><i className="ri-download-2-line text-gray-600 text-sm"></i></div>
                        Yedek Al
                      </button>
                      <button onClick={() => { setShowImportModal(true); setShowToolsMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors cursor-pointer">
                        <div className="w-7 h-7 flex items-center justify-center bg-cyan-50 rounded-lg"><i className="ri-upload-cloud-line text-cyan-600 text-sm"></i></div>
                        Yedek Yükle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Navigation Toggle */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              <button
                onClick={() => setShowMobileMenu((v) => !v)}
                className={`p-2 rounded-lg transition-all flex items-center justify-center border ${showMobileMenu ? 'bg-gray-100 border-gray-300 text-gray-900' : 'text-gray-700 hover:bg-gray-100 border-transparent'}`}
                title="Menü"
              >
                <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
              
              {showMobileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl overflow-hidden z-50 flex flex-col" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <div className="py-2 flex flex-col">
                    <button onClick={() => { navigate('/appointments'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Tedavi Takibi
                    </button>
                    <button onClick={() => { navigate('/calendar'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Takvim
                    </button>
                    <div className="h-px bg-gray-100 my-1 mx-4"></div>
                    <div className="px-5 pt-2 pb-1 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Araçlar
                    </div>
                    <button onClick={() => { navigate('/analytics'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Analizler
                    </button>
                    <button onClick={() => { navigate('/cari'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Cari Hesaplar
                    </button>
                    <button onClick={() => { setShowCariModal(true); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Cari Ekle
                    </button>
                    <button onClick={() => { navigate('/sms-templates'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Mesaj Şablonları
                    </button>
                    <button onClick={() => { navigate('/pricing'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Ücretler
                    </button>
                    <div className="h-px bg-gray-100 my-1 mx-4"></div>
                    <button onClick={() => { setShowExportModal(true); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Yedek Al
                    </button>
                    <button onClick={() => { setShowImportModal(true); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Yedek Yükle
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={() => navigate('/new-record')} className="px-3 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all flex items-center gap-1 md:gap-2 whitespace-nowrap cursor-pointer text-xs md:text-sm">
              <i className="ri-add-line text-base md:text-lg hidden md:block"></i>Yeni Kayıt
            </button>
          </div>
        </div>
      </nav>

      {successMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-500 text-white rounded-xl flex items-center gap-2 text-sm">
          <i className="ri-check-line text-xl"></i>{successMessage}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-10">

        {/* STATS */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Hasta Kayıtlarım</h2>
          <div className="flex flex-wrap gap-4">
            {[
              { icon: 'ri-group-line', color: 'teal', label: 'Toplam Hasta', value: patientGroups.length },
              { icon: 'ri-surgical-mask-line', color: 'cyan', label: 'Toplam Kayıt', value: surgeries.length },
              { icon: 'ri-tooth-line', color: 'emerald', label: 'Toplam İmplant', value: totalTeeth },
              { icon: 'ri-file-text-line', color: 'amber', label: 'Toplam Not', value: totalNotes },
            ].map((stat) => (
              <div key={stat.label} className="px-4 py-2.5 bg-white rounded-xl flex items-center gap-3">
                <div className={`w-10 h-10 bg-${stat.color}-50 rounded-lg flex items-center justify-center`}>
                  <i className={`${stat.icon} text-${stat.color}-600 text-lg`}></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SEARCH & FILTER */}
        {patientGroups.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hasta adı, klinik, tarih veya VDK kodu ile ara..."
                className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-6 h-6 flex items-center justify-center">
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            {isFiltering && (
              <p className="text-sm text-gray-500 px-1">
                <strong className="text-gray-900">{filteredGroups.length}</strong> hasta bulundu
              </p>
            )}
          </div>
        )}

        {/* RECORDS */}
        {patientGroups.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz kayıt yok</h3>
            <p className="text-gray-600 mb-6">İlk vaka kaydınızı oluşturun</p>
            <button onClick={() => navigate('/new-record')}
              className="px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all inline-flex items-center gap-1 md:gap-2 whitespace-nowrap cursor-pointer text-sm md:text-base">
              <i className="ri-add-line text-base md:text-lg hidden md:block"></i>Yeni Kayıt Oluştur
            </button>
          </div>
        ) : filteredGroups.length === 0 && isFiltering ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sonuç bulunamadı</h3>
            <button onClick={() => { setSearchQuery(''); }}
              className="px-5 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer">
              Filtreleri Temizle
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredGroups.map((group) => {
              const isExpanded = expandedPatients.has(group.patientName);
              const latestSurgery = group.surgeries[0];

              return (
                <div key={group.patientName} className="bg-white rounded-2xl overflow-hidden">
                  {/* PATIENT HEADER */}
                  <div onClick={() => togglePatient(group.patientName)}
                    className="px-6 py-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900">{group.patientName}</h3>
                          {latestSurgery?.clinic && (
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-medium whitespace-nowrap">
                              {latestSurgery.clinic}
                            </span>
                          )}
                          {group.surgeries.length > 0 && group.surgeries.every((s) => s.completed) && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold whitespace-nowrap">
                              Tedavi Tamamlandı
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setNotesPopupPatient(group.patientName); }}
                            className="px-2 py-0.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded text-xs font-medium whitespace-nowrap cursor-pointer transition-colors"
                          >
                            Notlar{group.notes.length > 0 && ` (${group.notes.length})`}
                          </button>

                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">

                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setMessagingPatient({ name: group.patientName, phone: latestSurgery?.phone || '' }); }}
                        title="WhatsApp Gönder"
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-base"></i>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/new-record', { state: { prefillPatient: latestSurgery } });
                        }}
                        title="Bu Hasta İçin Yeni Kayıt Ekle"
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all cursor-pointer"
                      >
                        <i className="ri-add-line text-base"></i>
                      </button>
                      <p className="text-sm text-gray-500 hidden md:block ml-1">
                        {group.surgeries.length} Kayıt
                      </p>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-xl text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  {/* SURGERIES TABLE */}
                  {isExpanded && (
                    <div className="px-6 pb-4 space-y-4">
                      {group.surgeries.map((surgery) => (
                        <div key={surgery.id} className="border border-gray-100 rounded-xl overflow-hidden">
                          {/* Surgery Header */}
                          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                            <span className="font-semibold text-gray-800 text-sm">
                                <i className="ri-calendar-line mr-1.5 text-teal-600"></i>
                                {new Date(surgery.operation_date).toLocaleDateString('tr-TR')}
                            </span>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => navigate(`/edit-record/${surgery.id}`)}
                                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all cursor-pointer"
                                title="Kaydı Düzenle"
                              >
                                <i className="ri-edit-line text-sm"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSurgery(surgery.id, surgery.patient_name)}
                                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </div>

                          {/* Teeth Table */}
                            {surgery.teeth && surgery.teeth.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-teal-50 text-teal-800">
                                    {['Diş No', 'İmplant Markası'].map((h) => (
                                      <th key={h} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap border-r border-teal-100">{h}</th>
                                    ))}
                                    <th className="px-4 py-2.5 text-left font-semibold whitespace-nowrap border-r border-teal-100">
                                      <span className="flex items-center gap-1.5">
                                        VDK
                                        <VDKLegendModal variant="icon" />
                                      </span>
                                    </th>
                                    <th className="px-4 py-2.5 text-left font-semibold whitespace-nowrap border-r border-teal-100">Ücret</th>
                                    <th className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">Durum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {surgery.teeth.map((tooth, idx) => {
                                    const fee = pricingDB.calcToothFee(pricing, { soket: tooth.soket, greft: tooth.greft, kemik: tooth.kemik, hacim: tooth.hacim, healing: tooth.healing });
                                    return (
                                    <tr key={tooth.tooth_number} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${tooth.failed ? 'opacity-75' : ''}`}>
                                      <td className="px-4 py-2.5 font-bold text-gray-900 border-r border-gray-100 whitespace-nowrap">
                                        {tooth.failed && <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5 mb-0.5"></span>}
                                        #{tooth.tooth_number}
                                      </td>

                                      <td className="px-4 py-2.5 border-r border-gray-100 whitespace-nowrap">
                                        {tooth.implant_brand ? (
                                          <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-xs font-medium flex items-center gap-1 w-fit">
                                            <i className="ri-award-line" style={{ fontSize: '10px' }}></i>
                                            {tooth.implant_brand}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400 text-xs">—</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2.5 border-r border-gray-100 whitespace-nowrap">
                                        <span className={`font-mono font-bold tracking-widest text-sm ${tooth.failed ? 'text-red-400 line-through' : 'text-teal-700'}`}>{tooth.vdk}</span>
                                      </td>
                                      <td className="px-4 py-2.5 border-r border-gray-100 whitespace-nowrap">
                                        <span className={`text-sm font-bold font-mono ${tooth.failed ? 'text-gray-400 line-through' : 'text-emerald-600'}`}>
                                          {pricing.currency}{fee.toLocaleString('tr-TR')}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5 whitespace-nowrap">
                                        {tooth.failed ? (
                                          <div className="flex items-center gap-2">
                                            <div className="flex flex-col">
                                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1 w-fit">
                                                <i className="ri-close-circle-line text-sm"></i>
                                                Fail
                                              </span>
                                              <span className="text-xs text-gray-500 mt-0.5">
                                                {tooth.fail_date ? new Date(tooth.fail_date).toLocaleDateString('tr-TR') : '—'}
                                              </span>
                                              {tooth.fail_days != null && (
                                                <span className="text-xs text-red-500 font-medium">{tooth.fail_days} gün</span>
                                              )}
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleFailTooth(surgery, tooth.tooth_number)}
                                              title="Fail kaydını geri al"
                                              className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 hover:bg-gray-100 rounded transition-all cursor-pointer flex-shrink-0"
                                            >
                                              <i className="ri-arrow-go-back-line text-xs"></i>
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => handleFailTooth(surgery, tooth.tooth_number)}
                                            className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 hover:border-red-300 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                                          >
                                            <i className="ri-close-circle-line text-sm"></i>
                                            Fail
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {/* Implant Count + Surgery Total */}
                              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-100 text-teal-700 rounded-lg text-sm font-bold">
                                  <i className="ri-tooth-line text-sm"></i>
                                  {surgery.teeth.length}
                                </span>
                                {(() => {
                                  const activeTeeth = surgery.teeth.filter((t) => !t.failed);
                                  const totalFee = activeTeeth.reduce((sum, t) => sum + pricingDB.calcToothFee(pricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: t.healing }), 0);
                                  const borcTxs = cariTxs
                                    .filter((tx) => tx.surgery_id === surgery.id && tx.type === 'borc');
                                  const latestBorcTx = borcTxs.length > 0
                                    ? borcTxs.reduce((latest, tx) =>
                                      new Date(tx.created_at).getTime() > new Date(latest.created_at).getTime() ? tx : latest)
                                    : null;
                                  const netFee = latestBorcTx ? latestBorcTx.amount : totalFee;
                                  const discount = totalFee - netFee;

                                  return (
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-semibold text-gray-600">
                                        Toplam ({activeTeeth.length} aktif diş)
                                      </span>
                                      {discount > 0 ? (
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-bold text-gray-400 line-through">
                                            {pricing.currency}{totalFee.toLocaleString('tr-TR')}
                                          </span>
                                          <span className="text-base font-bold text-emerald-600 flex items-center gap-1">
                                            <i className="ri-percent-line text-orange-500 text-sm" title={`${pricing.currency}${discount.toLocaleString('tr-TR')} İndirim`}></i>
                                            {pricing.currency}{netFee.toLocaleString('tr-TR')}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="text-base font-bold text-emerald-600">
                                          {pricing.currency}{totalFee.toLocaleString('tr-TR')}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 px-4 py-3">Bu kayıtta diş verisi yok.</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* NOTES POPUP PANEL */}
      {notesPopupPatient && (() => {
        const popupNotes = patientNotes.filter((n) => n.patient_id === notesPopupPatient);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setNotesPopupPatient(null)}>
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-white font-bold text-base">{notesPopupPatient}</h3>
                  <p className="text-amber-100 text-xs mt-0.5">{popupNotes.length} not</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { openNoteModal(notesPopupPatient); }}
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <i className="ri-add-line"></i>Not Ekle
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotesPopupPatient(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 text-white hover:bg-white/30 transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
              </div>
              {/* Notes List */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {popupNotes.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-file-text-line text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-sm text-gray-500">Henüz not eklenmemiş</p>
                    <button
                      type="button"
                      onClick={() => openNoteModal(notesPopupPatient)}
                      className="mt-3 px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition-all inline-flex items-center gap-1.5 cursor-pointer"
                    >
                      <i className="ri-add-line"></i>İlk notu ekle
                    </button>
                  </div>
                ) : (
                  popupNotes.map((note) => (
                    <div key={note.id} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getNoteTypeColor(note.note_type)}`}>{getNoteTypeLabel(note.note_type)}</span>
                            <span className="text-xs text-gray-500">{new Date(note.note_date).toLocaleDateString('tr-TR')}</span>
                          </div>
                          <h4 className="font-semibold text-gray-900 text-sm mb-1">{note.title}</h4>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{note.content}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => openNoteModal(notesPopupPatient, note)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-white rounded-lg transition-all cursor-pointer" title="Düzenle">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => handleDeleteNote(note.id)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-white rounded-lg transition-all cursor-pointer" title="Sil">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* NOTE MODAL */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{editingNote ? 'Notu Düzenle' : 'Yeni Not'}</h3>
                <p className="text-sm text-gray-500">{notePatientId}</p>
              </div>
              <button onClick={closeNoteModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tarih</label>
                  <input type="date" value={noteFormData.note_date}
                    onChange={(e) => setNoteFormData({ ...noteFormData, note_date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Not Türü</label>
                  <select value={noteFormData.note_type} onChange={(e) => setNoteFormData({ ...noteFormData, note_type: e.target.value as PatientNote['note_type'] })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                    <option value="general">Genel</option>
                    <option value="checkup">Kontrol</option>
                    <option value="complication">Komplikasyon</option>
                    <option value="treatment">Tedavi</option>
                    <option value="followup">Takip</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Başlık</label>
                <input type="text" value={noteFormData.title} onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
                  placeholder="Not başlığı..." className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">İçerik</label>
                <textarea value={noteFormData.content} onChange={(e) => setNoteFormData({ ...noteFormData, content: e.target.value.slice(0, 500) })}
                  placeholder="Not içeriği..." rows={5} maxLength={500}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none" />
                <p className="text-xs text-gray-400 mt-1 text-right">{noteFormData.content.length}/500</p>
              </div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button onClick={closeNoteModal} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 whitespace-nowrap cursor-pointer text-sm">İptal</button>
                <button onClick={handleSaveNote} disabled={savingNote || !noteFormData.title.trim() || !noteFormData.content.trim()}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer text-sm">
                  {savingNote ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Kaydediliyor...</> : <><i className="ri-save-line"></i>{editingNote ? 'Güncelle' : 'Kaydet'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && <ExportModal onClose={() => setShowExportModal(false)} />}
      {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} onImported={loadData} />}

      {showCariModal && (
        <CariClinicModal
          onSaved={() => {
            setShowCariModal(false);
            setSuccessMessage('Cari hesap eklendi!');
            setTimeout(() => setSuccessMessage(''), 3000);
          }}
          onClose={() => setShowCariModal(false)}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel="Sil"
          variant="danger"
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {messagingPatient && (
        <SendMessageModal
          patientName={messagingPatient.name}
          initialPhone={messagingPatient.phone}
          onClose={() => setMessagingPatient(null)}
        />
      )}
    </div>
  );
}
